import type { WebsiteVisitsResultReport } from '@/api/generated/schemas/websiteVisitsResultReport';

/**
 * Agency › Website: types and number/date wording shared by the tab's cards. Visit numbers come from PostHog
 * (a "visit" = one browser tab, never "sessions" or "unique visitors"); demo request numbers from our DB.
 */

/** The loaded report (the API sends `null` only alongside `connected: false` or a key error). */
export type WebsiteVisitsReport = NonNullable<WebsiteVisitsResultReport>;
export type WebsiteRecentVisit = WebsiteVisitsReport['recentVisits'][number];
export type WebsiteVisitsRange = WebsiteVisitsReport['period']['range'];

export const WEBSITE_RANGE_OPTIONS: ReadonlyArray<{ value: WebsiteVisitsRange; label: string }> = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

export const DEFAULT_WEBSITE_RANGE: WebsiteVisitsRange = '30d';

export function isWebsiteVisitsRange(value: unknown): value is WebsiteVisitsRange {
  return WEBSITE_RANGE_OPTIONS.some((option) => option.value === value);
}

/** "previous 30 days" — the comparison window named under each summary number. */
export function previousPeriodLabel(range: WebsiteVisitsRange): string {
  return `previous ${range.replace('d', '')} days`;
}

export const DEVICE_LABEL: Record<NonNullable<WebsiteRecentVisit['device']>, string> = {
  mobile: 'Mobile',
  desktop: 'Desktop',
  tablet: 'Tablet',
};

export const LOCALE_LABEL: Record<WebsiteRecentVisit['locale'], string> = {
  en: 'English',
  vi: 'Vietnamese',
};

const COUNT = new Intl.NumberFormat('en-US');

export function formatCount(value: number): string {
  return COUNT.format(value);
}

/**
 * 0.0094 → "0.9%", 0.25 → "25%": one decimal below 10%, and "<0.1%" for a tiny but real rate, so a small rate
 * never reads "1%" or "0%".
 */
export function formatPercent(rate: number | null | undefined): string {
  if (rate == null || !Number.isFinite(rate)) return '—';
  const percent = rate * 100;
  if (percent > 0 && percent < 0.05) return '<0.1%';
  if (percent > 0 && percent < 10) return `${(Math.round(percent * 10) / 10).toFixed(1).replace(/\.0$/, '')}%`;
  return `${Math.round(percent)}%`;
}

/** "1 visit" / "3 visits". */
export function plural(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

export type WebsiteChangeTone = 'up' | 'down' | 'flat' | 'none';

export interface WebsiteChange {
  text: string;
  tone: WebsiteChangeTone;
}

/**
 * Change vs the previous period as shown under a summary number. Counts and seconds are relative (0.25 →
 * "+25%"), rates are points (0.05 → "+5 points"). The API sends null when the previous value is 0 or unknown:
 * then `current` / `previous` tell "Up from 0" (real growth) from "Nothing to compare yet" (no earlier data).
 */
export function formatChange(
  value: number | null | undefined,
  kind: 'relative' | 'points',
  values?: { current: number | null; previous: number | null }
): WebsiteChange {
  if (value == null || !Number.isFinite(value)) {
    if (values?.previous === 0 && values.current != null) {
      return values.current > 0 ? { text: 'Up from 0', tone: 'up' } : { text: 'Same as before', tone: 'flat' };
    }
    return { text: 'Nothing to compare yet', tone: 'none' };
  }
  const percent = value * 100;
  const rounded = kind === 'points' && Math.abs(percent) < 10 ? Math.round(percent * 10) / 10 : Math.round(percent);
  if (rounded === 0) return { text: 'Same as before', tone: 'flat' };
  const sign = rounded > 0 ? '+' : '−';
  const amount = `${sign}${Math.abs(rounded)}`;
  return { text: kind === 'points' ? `${amount} points` : `${amount}%`, tone: rounded > 0 ? 'up' : 'down' };
}

const DAY_LABEL = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** "2026-10-28" (a calendar day in the report's zone) → "Oct 28". Read as UTC so the day never shifts. */
export function formatReportDay(date: string): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? date : DAY_LABEL.format(parsed);
}

/** "Oct 28, 2:20 PM" in the viewer's own zone (the browser's, which is also the report's). */
export function formatVisitTime(iso: string, timeZone?: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: 'h12',
    ...(timeZone ? { timeZone } : {}),
  }).format(parsed);
}

/** Index of the page the visitor was on when the demo request was sent (the last one opened at or before it). */
export function demoSentPageIndex(visit: Pick<WebsiteRecentVisit, 'pages' | 'demoRequestedAt'>): number | null {
  if (!visit.demoRequestedAt) return null;
  const sentAt = Date.parse(visit.demoRequestedAt);
  for (let position = visit.pages.length - 1; position >= 0; position -= 1) {
    if (Date.parse(visit.pages[position]!.at) <= sentAt) return position;
  }
  return null;
}
